import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../../pdc-platform/src/ts/Glob';
import * as gridLayout from '../../../pdc-platform/src/tsx/layout/gridLayout';
import { WidgetBase } from './WidgetBase';
import { config } from '../../../pdc-platform/src/ts/config';
import { IValuesCollection } from './appTheme';
import * as Utilities from '../ts/Utilities';

import { IWidget, IWidgetValues } from '../../../pdc-platform/src/ts/Interfaces';

const NESTED_LAYOUT_WIDGET_TYPE = 'pdc-widget-layout';

export class WidgetData<TOptions = any> {
    private static loaded: { [name: string]: boolean } = {};
    public static loading: { [name: string]: boolean } = {};
    public static error: { [name: string]: boolean } = {};

    public widget: WidgetBase;
    public loading: string;

    private _def: JQuery.Deferred<any>;
    private valuesToSave: { [typeName: string]: any };

    constructor(
        public values: IWidgetValues,
        public readonly glob: IGlob,
        public readonly tabID: number,
        public readonly theme: IValuesCollection,

        public type?: string,
        public id?: number,
        public cid?: number,
        public locked?: { [type: string]: any },

        ignoreValidation?: boolean,
        private elements?: any[],

        public layout?: any,
        public icon?: string,
        public tabBackColor?: string,
        public tabTextColor?: string,
        public options?: TOptions
    ) {
        this.valuesToSave = _.clone(values);

        if (type != null) {
            this._def = this.setWidgetType(type, ignoreValidation);
        }

        if (id == null && cid == null) {
            this.cid = Utilities.getNewID();
        }
    }

    public static getEmpty(glob: IGlob, theme?: IValuesCollection, tabID?: number) {
        return new WidgetData({}, glob, tabID, theme);
    }

    public get def(): JQuery.Deferred<any> {
        return this._def;
    }

    public getString() {
        return JSON.stringify(this.getData());
    }

    public getPrettyString() {
        return JSON.stringify(this.getData(), null, 4);
    }

    public getData(): IWidget {
        return {
            type: this.type,
            values: this.values,
            cid: this.cid,
            id: this.id,
            elements: this.elements,
            layout: this.layout,
            icon: this.icon,
            tabBackColor: this.tabBackColor,
            tabTextColor: this.tabTextColor,
            options: this.options
        };
    }

    public getDataForSave() {
        const data = WidgetData.normalizeLayout(this.getData());

        if (config.SAVE_WIDGET_CONFIG_DEFAULTS === false) {
            const widgetName = data.values[this.type] ? data.values[this.type].name : null;

            data.values = this.valuesToSave;

            if (widgetName !== null) {
                data.values[this.type].name = widgetName;
            }
        }

        return data;
    }

    public clone() {
        return WidgetData.getFromObj(this.getDataForSave(), this.glob, true, this.theme, this.tabID);
    }

    public setWidgetType(type, ignoreValidation) {
        const def = $.Deferred<any>();

        if (WidgetData.loaded[type] === true) {
            this.type = type;
            this.widget = this.getWidgetInstance(type, this.glob, ignoreValidation);
            def.resolve();
            return def;
        }

        const Constructor = WidgetBase.getConstructorByName(type);

        if (Constructor == null) {
            def.reject();
            return def;
        }

        WidgetData.loading[type] = true;
        WidgetData.error[type] = false;
        delete this.widget;
        delete this.type;

        this.loading = type;

        this.glob.AppActions.widgetModuleLoadStart(type);

        Constructor.load()
            .done(() => {
                this.type = type;

                if (this.loading === type) {
                    delete this.loading;
                }

                WidgetData.loaded[type] = true;
                WidgetData.loading[type] = false;
                this.widget = this.getWidgetInstance(type, this.glob, ignoreValidation);
                this.glob.AppActions.widgetModuleLoadFinish(type);
                def.resolve();
            })
            .fail(() => {
                if (this.loading === type) {
                    delete this.loading;
                }

                WidgetData.loading[type] = false;
                WidgetData.error[type] = true;
                delete this.type;

                console.log("An error occured while creating a widget of type '" + type + "'.");

                this.glob.AppActions.widgetModuleLoadFinish(type);
                def.reject();
            });

        return def;
    }

    public setFieldValue(type: string, fieldName: string, value) {
        if (this.values[type] == null || this.type !== type) {
            return;
        }

        const res = this.widget.setFieldValue(fieldName, value);

        this.values[type] = res.values;
        this.valuesToSave[type] = res.valuesToSave;
    }

    public setFieldValues(type: string, values) {
        if (this.values[type] == null || this.type !== type) {
            return;
        }

        const res = this.widget.setFieldValues(values);

        this.values[type] = res.values;
        this.valuesToSave[type] = res.valuesToSave;
    }

    public setLayout(layout) {
        this.layout = layout;
    }

    public setLock(type: string, fieldName: string, value) {
        const locked = this.locked[type];

        if (locked == null || this.type !== type) {
            return;
        }

        locked[fieldName] = value;
        this.widget.setLock(fieldName, value);
    }

    private getWidgetInstance(type: string, glob: IGlob, ignoreValidation = false) {
        const Constructor = WidgetBase.getConstructorByName(type);

        if (Constructor == null) {
            throw new Error("Widget module '" + type + "' is not available");
        }

        // This is a workaround to pass
        // inherited theme parameter to widget constructor.
        // ___theme should be removed after instantiation
        const values = this.valuesToSave[type] || {};

        values['___theme'] = this.theme;
        values['___tabID'] = this.tabID;
        values['id'] = this.id;

        const result = new Constructor(values, null, ignoreValidation, glob);

        // Make sure ___theme field is removed,
        // so it doesn't go further
        delete result.values['___theme'];
        delete result.valuesToSave['___theme'];
        delete values['___tabID'];
        delete result.valuesToSave['___tabID'];

        this.values = {
            [type]: result.values
        };

        this.valuesToSave = {
            [type]: result.valuesToSave
        };

        return result;
    }

    public static getFromObj(
        obj: IWidget,
        glob: IGlob,
        ignoreValidation: boolean = false,
        theme: IValuesCollection = null,
        tabID?: number
    ) {
        obj = JSON.parse(JSON.stringify(obj));

        return new WidgetData(
            obj.values,
            glob,
            tabID,
            theme,
            obj.type,
            obj.id,
            obj.cid,
            undefined,
            ignoreValidation,
            obj.elements,
            obj.layout,
            obj.icon,
            obj.tabBackColor,
            obj.tabTextColor,
            obj.options
        );
    }

    public static normalizeData(data, glob: IGlob): IWidget {
        const inst = WidgetData.getFromObj(data, glob);

        return inst.getData();
    }

    public static normalizeForSaveData(data: IWidget, glob: IGlob) {
        const inst = WidgetData.getFromObj(data, glob);

        return inst.getDataForSave();
    }

    private static normalizeLayout(data: IWidget) {
        if (data.type === NESTED_LAYOUT_WIDGET_TYPE) {
            let newLayout = _.clone(data.layout);

            if (newLayout == null) {
                newLayout = {
                    columns: [{ ratio: 1 }],
                    rows: [{ ratio: 1 }],
                    pos: [[0, 0]],
                    size: [[1, 1]],
                    layoutKind: 'static'
                };
            } else {
                const widgets = data.values[NESTED_LAYOUT_WIDGET_TYPE].widgets;
                const existWidgetsInLayout = newLayout.pos.length;
                const numOfNewWidgets = widgets.length - existWidgetsInLayout;

                if (numOfNewWidgets > 0) {
                    for (let i = existWidgetsInLayout; i < widgets.length; i++) {
                        const firstEmptyCell = gridLayout.getFirstEmptyCell(newLayout);

                        if (firstEmptyCell != null) {
                            newLayout = gridLayout.elementAttach(
                                newLayout,
                                i,
                                firstEmptyCell.x,
                                firstEmptyCell.y,
                                false,
                                true
                                //() => {
                                //this.actions.widgetRemove(
                                //    id
                                //);
                                //}
                            );
                        }
                    }
                }
            }

            data.layout = _.clone(newLayout);
        } else {
            if (data.layout != null && data.layout.layoutKind == 'static') {
                data.layout = null; //IL: should be tested: WorkItem [102090]: Some problem with changing the TYPE of the existed widget = BUGS
            }
        }

        return data;
    }

    public static getFromJSON(
        str: string,
        glob: IGlob,
        renewIdsAndCids?: boolean,
        theme?: IValuesCollection
    ): {
        result: boolean;
        widgetData?: WidgetData;
    } {
        const obj = JSON.parse(str) as IWidget;

        if (obj.type != null && typeof obj.type !== 'string') {
            return { result: false };
        }

        if (typeof obj.values !== 'object') {
            return { result: false };
        }

        if (renewIdsAndCids === true) {
            Utilities.renewIDsAndCIDs(obj);
        }

        return {
            result: true,
            widgetData: WidgetData.getFromObj(
                {
                    values: obj.values,
                    type: obj.type,
                    id: obj.id,
                    cid: obj.cid,

                    elements: obj.elements,
                    layout: obj.layout,
                    icon: obj.icon,
                    tabBackColor: obj.tabBackColor,
                    tabTextColor: obj.tabTextColor,
                    options: obj.options
                },
                glob,
                false,
                theme
            )
        };
    } 
}
